// ./src/components/features/AssignRunningMessageModal/AssignRunningMessageModal.tsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Stack,
  Text,
  Group,
  Checkbox,
  ScrollArea,
  Alert,
  Box,
  Divider,
} from "@mantine/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { RunningMessage } from "@/utils/api";
import { useDisplays, useDisplayRunningMessages } from "@/hooks";
import { formatDate, formatTimeRange } from "@/utils";

import { AppModal, AppIcon, LoadingIndicator } from "@/components/generic";

dayjs.extend(utc);

export interface AssignRunningMessageModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Running message to assign */
  runningMessage: RunningMessage;

  /** Optional venue ID to filter displays */
  //venueId?: string;
}

interface DisplayOperationState {
  displayId: string;
  loading: boolean;
  error: string | null;
}

export const AssignRunningMessageModal: React.FC<
  AssignRunningMessageModalProps
> = ({ opened, onClose, runningMessage /*, venueId*/ }) => {
  // State for tracking operations per display
  const [operations, setOperations] = useState<
    Record<string, DisplayOperationState>
  >({});

  // Fetch all displays
  const { displays, loading: displaysLoading } = useDisplays({
    //venueId,
    autoLoad: true,
  });

  // Fetch assignments for this running message
  const {
    displayRunningMessages,
    loading: assignmentsLoading,
    create: createAssignment,
    remove: removeAssignment,
  } = useDisplayRunningMessages({
    runningMessageId: runningMessage.id,
    autoLoad: true,
  });

  // Create a set of assigned display IDs for quick lookup
  const assignedDisplayIds = useMemo(() => {
    return new Set(
      displayRunningMessages
        .filter((drm) => drm.displayId)
        .map((drm) => drm.displayId as string)
    );
  }, [displayRunningMessages]);

  // Get assignment ID for a display (for removal)
  const getAssignmentId = (displayId: string): string | null => {
    const assignment = displayRunningMessages.find(
      (drm) => drm.displayId === displayId
    );
    return assignment?.id || null;
  };

  // Reset operations state when modal closes
  useEffect(() => {
    if (!opened) {
      setOperations({});
    }
  }, [opened]);

  // Handle checkbox change
  const handleCheckboxChange = async (displayId: string, checked: boolean) => {
    // Set loading state for this display
    setOperations((prev) => ({
      ...prev,
      [displayId]: {
        displayId,
        loading: true,
        error: null,
      },
    }));

    try {
      if (checked) {
        // Create assignment
        const result = await createAssignment({
          displayId,
          runningMessageId: runningMessage.id,
          orderNo: 0,
        });

        if (result) {
          // Success - clear operation state
          setOperations((prev) => {
            const newOps = { ...prev };
            delete newOps[displayId];
            return newOps;
          });
        } else {
          // Error - show error message
          setOperations((prev) => ({
            ...prev,
            [displayId]: {
              displayId,
              loading: false,
              error: "Failed to assign running message",
            },
          }));
        }
      } else {
        // Remove assignment
        const assignmentId = getAssignmentId(displayId);
        if (assignmentId) {
          const success = await removeAssignment(assignmentId);

          if (success) {
            // Success - clear operation state
            setOperations((prev) => {
              const newOps = { ...prev };
              delete newOps[displayId];
              return newOps;
            });
          } else {
            // Error - show error message
            setOperations((prev) => ({
              ...prev,
              [displayId]: {
                displayId,
                loading: false,
                error: "Failed to remove assignment",
              },
            }));
          }
        }
      }
    } catch (error) {
      setOperations((prev) => ({
        ...prev,
        [displayId]: {
          displayId,
          loading: false,
          error: error instanceof Error ? error.message : "Operation failed",
        },
      }));
    }
  };

  // Clear error for a specific display
  const clearDisplayError = (displayId: string) => {
    setOperations((prev) => {
      const newOps = { ...prev };
      delete newOps[displayId];
      return newOps;
    });
  };

  const isLoading = displaysLoading || assignmentsLoading;

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="Assign Running Message to Displays"
      size="lg"
      actionType="close"
    >
      <Stack gap="md">
        {/* Running Message Info */}
        <Box>
          <Stack gap="xs">
            <Group gap="xs" align="center">
              <Text size="sm" fw={600}>
                {runningMessage.name || "Untitled Running Message"}
              </Text>
            </Group>

            {runningMessage.messageText && (
              <Text size="sm" c="dimmed" lineClamp={2}>
                {runningMessage.messageText}
              </Text>
            )}

            {/* Schedule Info */}
            {(runningMessage.startDatetime ||
              runningMessage.endDatetime ||
              runningMessage.startTime ||
              runningMessage.endTime) && (
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  Schedule:
                </Text>
                {(runningMessage.startDatetime ||
                  runningMessage.endDatetime) && (
                  <Text size="xs" c="dimmed">
                    {formatDate(runningMessage.startDatetime)} -{" "}
                    {formatDate(runningMessage.endDatetime)}
                  </Text>
                )}
                {(runningMessage.startTime || runningMessage.endTime) && (
                  <Text size="xs" c="dimmed">
                    Time:{" "}
                    {formatTimeRange(
                      dayjs(runningMessage.startTime).format("hh:mm A"),
                      dayjs(runningMessage.endTime).format("hh:mm A")
                    )}
                  </Text>
                )}
              </Stack>
            )}

            {/* Message Properties */}
            {runningMessage.scrollSpeed && (
              <Group gap="xs" wrap="wrap">
                {runningMessage.scrollSpeed && (
                  <Text size="xs" c="dimmed">
                    Speed: {runningMessage.scrollSpeed}
                  </Text>
                )}
              </Group>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Displays List */}
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Select Displays
          </Text>

          {isLoading ? (
            <Group justify="center" p="xl">
              <LoadingIndicator size={32} />
              <Text size="sm" c="dimmed">
                Loading displays...
              </Text>
            </Group>
          ) : displays.length === 0 ? (
            <Alert
              icon={<AppIcon iconName="InfoCircle" size={16} />}
              title="No displays found"
              color="blue"
            >
              <Text size="sm">
                No displays are available. Create a display first to assign
                running messages.
              </Text>
            </Alert>
          ) : (
            <ScrollArea.Autosize mah={400}>
              <Stack gap="xs">
                {displays.map((display) => {
                  const isAssigned = assignedDisplayIds.has(display.id);
                  const operation = operations[display.id];
                  const isOperationLoading = operation?.loading || false;
                  const operationError = operation?.error || null;

                  return (
                    <Box key={display.id}>
                      <Group gap="sm" wrap="nowrap" p="xs" align="flex-start">
                        {isOperationLoading ? (
                          <LoadingIndicator size={18} />
                        ) : (
                          <Checkbox
                            checked={isAssigned}
                            onChange={(e) =>
                              handleCheckboxChange(
                                display.id,
                                e.currentTarget.checked
                              )
                            }
                            disabled={isOperationLoading}
                            size="sm"
                            mt={3}
                          />
                        )}

                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="md" fw={600} truncate>
                            {display.name || "Untitled Display"}
                          </Text>
                          {display.description && (
                            <Text size="xs" c="dimmed" truncate>
                              {display.description}
                            </Text>
                          )}
                        </Box>

                        {isAssigned && !isOperationLoading && (
                          <AppIcon iconName="Check" size={16} color="green" />
                        )}
                      </Group>

                      {operationError && (
                        <Alert
                          icon={<AppIcon iconName="AlertCircle" size={16} />}
                          color="red"
                          mt={4}
                          withCloseButton
                          onClose={() => clearDisplayError(display.id)}
                        >
                          <Text size="xs">{operationError}</Text>
                        </Alert>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Stack>

        {/* Summary */}
        {displays.length > 0 && !isLoading && (
          <Group gap="xs">
            <AppIcon iconName="InfoCircle" size={16} />
            <Text size="xs" c="dimmed">
              {assignedDisplayIds.size} of {displays.length} displays assigned
            </Text>
          </Group>
        )}
      </Stack>
    </AppModal>
  );
};
